/**
 * The words on the upgrade screen.
 *
 * Chrome: every line here sits next to a number the player is about to spend, and a compare box
 * that reads as prose is a compare box nobody reads. See
 * [src/text/AGENTS.md](./AGENTS.md). The in-world half of this screen is the
 * perk titles and blurbs, which stay in their own files under `@/sim/progression/perks`, because
 * they are the perk rather than a label for it.
 *
 * Why a point could not be spent is `@/text/progressionText`, kept separate because it is an
 * exhaustive switch over a refusal union and this file is a flat list of labels.
 */

import { STAT_ORDER } from '@/sim/combat/statContract';
import type { StatId } from '@/sim/combat/statContract';

export const UPGRADE_STAT_POINTS = 'Stat points';
export const UPGRADE_PERK_POINTS = 'Perk points';

/**
 * The four stats, spelled for a screen.
 *
 * A full `Record` rather than a lookup that falls back, so a fifth stat added to `STAT_ORDER` with
 * no wording is a type error rather than a button labelled `charisma` in lower case, which is what
 * the panel printed before this existed.
 */
export const STAT_LABELS: Record<StatId, string> = {
  strength: 'Strength',
  perception: 'Perception',
  endurance: 'Endurance',
  agility: 'Agility',
};

/** Iteration order comes from the sim, so the screen and the save walk the stats the same way. */
export const UPGRADE_STAT_ORDER = STAT_ORDER;

export function upgradeLevel(level: number): string {
  return `Level ${String(level)}`;
}

export const UPGRADE_LEVEL_CAPPED = 'Nothing left to learn';

export function upgradeXpToNext(into: number, across: number): string {
  return `${String(into)} of ${String(across)} XP to the next level`;
}

export function upgradePoints(name: string, count: number): string {
  return `${name}: ${String(count)}`;
}

export const UPGRADE_NO_SELECTION = 'Pick a perk to see what it would change.';

export function upgradeAtLastRank(title: string): string {
  return `${title} is at its last rank.`;
}

/** A perk's own condition in brackets after the field it moves: "Reload time (while hurt)". */
export function upgradeCompareLabel(label: string, when: string): string {
  return when === '' ? label : `${label} (${when})`;
}

export function upgradeCompareValue(before: string, after: string): string {
  return `${before} to ${after}`;
}

export const UPGRADE_RESPEC = 'Respec';
export const UPGRADE_RESPEC_WARNING =
  'This puts every stat back to baseline and every perk back to nothing.';
export const UPGRADE_RESPEC_CONFIRM = 'Undo everything';
export const UPGRADE_RESPEC_CANCEL = 'Keep my build';
